import { useState } from 'react';
import FitScoreDisplay from './FitScoreDisplay';

export default function AlternativesDropdown({ alternatives, isPremiumOrAdmin }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  if (!isPremiumOrAdmin || !alternatives || alternatives.length === 0) {
    return null;
  }

  const sortedAlternatives = [...alternatives].sort((a, b) => b.confidence - a.confidence);
  const nonAgencyAlternatives = sortedAlternatives.filter(alt => !alt.isAgency);
  const agencyAlternatives = sortedAlternatives.filter(alt => alt.isAgency);

  return (
    <div style={{
      marginBottom: '32px',
      background: 'rgba(34, 139, 34, 0.15)',
      borderRadius: '12px',
      border: '2px solid rgba(152, 251, 152, 0.4)',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(34, 139, 34, 0.2)'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '20px',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(34, 139, 34, 0.25)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>🔍</span>
          <div style={{ textAlign: 'left' }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#98FB98',
              margin: 0,
              marginBottom: '4px',
              textShadow: '0 0 10px rgba(152, 251, 152, 0.7)'
            }}>
              All Alternative Companies Considered
            </h4>
            <p style={{
              fontSize: '13px',
              color: 'rgba(152, 251, 152, 0.75)',
              margin: 0
            }}>
              {nonAgencyAlternatives.length} potential matches analyzed
              {agencyAlternatives.length > 0 && ` + ${agencyAlternatives.length} agencies filtered`}
            </p>
          </div>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(152, 251, 152, 0.2)',
            border: '1px solid rgba(152, 251, 152, 0.4)',
            borderRadius: '4px',
            color: '#98FB98',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            PREMIUM
          </span>
        </div>
        <div style={{
          fontSize: '24px',
          color: '#98FB98',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s ease'
        }}>
          ▼
        </div>
      </button>

      {isOpen && (
        <div style={{ padding: '0 20px 20px 20px' }}>
          {nonAgencyAlternatives.length > 0 && (
            <div style={{ marginBottom: agencyAlternatives.length > 0 ? '24px' : '0' }}>
              <h5 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#98FB98',
                marginBottom: '16px',
                textShadow: '0 0 8px rgba(152, 251, 152, 0.6)'
              }}>
                Potential Client Companies ({nonAgencyAlternatives.length})
              </h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {nonAgencyAlternatives.map((alt, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'rgba(34, 139, 34, 0.2)',
                      borderRadius: '10px',
                      border: '2px solid rgba(152, 251, 152, 0.3)',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div
                      onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      style={{
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(34, 139, 34, 0.3)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#98FB98',
                            textShadow: '0 0 10px rgba(152, 251, 152, 0.5)'
                          }}>
                            {index + 1}. {alt.name}
                          </span>
                          <span className={`badge ${
                            alt.confidence > 70 ? 'badge-high' :
                            alt.confidence > 40 ? 'badge-medium' :
                            'badge-low'
                          }`} style={{ fontSize: '13px' }}>
                            {alt.confidence}%
                          </span>
                          {alt.isEquallyLikely && (
                            <span style={{ padding: '3px 8px', background: 'rgba(255, 215, 0, 0.3)', border: '1px solid rgba(255, 215, 0, 0.6)', borderRadius: '4px', fontSize: '10px', fontWeight: '700', color: '#FFD700', textTransform: 'uppercase' }}>
                              Equally Likely
                            </span>
                          )}
                          {alt.techInferred && (
                            <span title="Technology inferred from industry standards" style={{ padding: '3px 6px', background: 'rgba(255, 200, 100, 0.2)', border: '1px solid rgba(255, 200, 100, 0.4)', borderRadius: '4px', fontSize: '9px', fontWeight: '600', color: 'rgba(255, 200, 100, 0.9)' }}>
                              📊 Inferred
                            </span>
                          )}
                          {alt.evidenceQuality && (
                            <span style={{ padding: '3px 6px', background: alt.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.2)' : alt.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.2)' : 'rgba(255, 107, 107, 0.2)', border: `1px solid ${alt.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.5)' : alt.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.5)' : 'rgba(255, 107, 107, 0.5)'}`, borderRadius: '4px', fontSize: '9px', fontWeight: '600', color: alt.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.95)' : alt.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.9)' : 'rgba(255, 107, 107, 0.9)' }}>
                              {alt.evidenceQuality === 'CONFIRMED' ? '✓' : alt.evidenceQuality === 'INFERRED' ? '~' : '!'} {alt.evidenceQuality}
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#98FB98',
                          fontWeight: '600'
                        }}>
                          Fit: {((alt.locationMatch + alt.techMatch + alt.businessFit + alt.hiringPattern) / 4).toFixed(1)}/5
                        </div>
                      </div>

                      <p style={{
                        fontSize: '13px',
                        color: 'rgba(152, 251, 152, 0.85)',
                        lineHeight: '1.6',
                        margin: 0,
                        marginBottom: '12px'
                      }}>
                        {alt.reasoning}
                      </p>

                      {alt.phraseMatches && alt.phraseMatches.length > 0 && (
                        <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(152, 251, 152, 0.08)', borderRadius: '6px', border: '1px solid rgba(152, 251, 152, 0.2)' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#98FB98', marginBottom: '6px' }}>🎯 Matched Phrases:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {alt.phraseMatches.map((phrase, pIdx) => (
                              <span key={pIdx} style={{ padding: '3px 6px', background: 'rgba(152, 251, 152, 0.15)', border: '1px solid rgba(152, 251, 152, 0.3)', borderRadius: '3px', fontSize: '10px', color: 'rgba(152, 251, 152, 0.9)', fontFamily: 'monospace' }}>
                                {phrase}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {alt.expansionSignals && alt.expansionSignals.length > 0 && (
                        <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(255, 215, 0, 0.08)', borderRadius: '6px', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#FFD700', marginBottom: '6px' }}>📈 Growth Signals:</div>
                          <ul style={{ margin: 0, paddingLeft: '16px', color: 'rgba(255, 215, 0, 0.85)', fontSize: '11px', lineHeight: '1.5' }}>
                            {alt.expansionSignals.map((signal, sIdx) => (
                              <li key={sIdx}>{signal}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {expandedIndex !== index && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          color: 'rgba(152, 251, 152, 0.6)',
                          fontStyle: 'italic'
                        }}>
                          Click to view detailed scoring breakdown ▼
                        </div>
                      )}
                    </div>

                    {expandedIndex === index && (
                      <div style={{
                        padding: '0 16px 16px 16px',
                        borderTop: '1px solid rgba(152, 251, 152, 0.2)'
                      }}>
                        <FitScoreDisplay company={alt} showDetailed={true} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {agencyAlternatives.length > 0 && (
            <div>
              <h5 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: 'rgba(152, 251, 152, 0.7)',
                marginBottom: '16px',
                textShadow: '0 0 8px rgba(152, 251, 152, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🚫</span>
                Filtered Agencies ({agencyAlternatives.length})
              </h5>
              <div style={{
                background: 'rgba(139, 34, 34, 0.15)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 107, 107, 0.3)'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(152, 251, 152, 0.75)',
                  marginBottom: '12px',
                  lineHeight: '1.6'
                }}>
                  These companies were identified as recruiting agencies and automatically excluded from client matches.
                  They have been added to the agency database.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {agencyAlternatives.map((agency, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(139, 34, 34, 0.2)',
                        border: '1px solid rgba(255, 107, 107, 0.4)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: 'rgba(255, 107, 107, 0.9)',
                        fontWeight: '600'
                      }}
                    >
                      {agency.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
